import { computed, type WritableComputedRef } from 'vue';
import { defineStore, type Store as PiniaStore } from 'pinia';

/**
 * The common Pinia store.
 */
export const useCommonStore = defineStore('common', {
  state: () => ({
    /**
     * The current partner.
     */
    partner: {
      name: '',
      store_root_domain: '',
      seller_email_domain: '',
      logo_url: '',
      favicon_url: '',
    } as Partner,

    /**
     * The current Pinia store, if set.
     */
    piniaStore: null as PiniaStore | null,

    /**
     * The submenu, if set.
     */
    submenu: null as SubmenuLink[] | null,

    /**
     * The model the overflow menu relates to, if set.
     */
    overflowMenuModel: null as Model | null,

    overflowMenuModelIndex: null as number | null,

    /**
     * The table meta information.
     */
    tableMeta: null as TableMeta | null,

    /**
     * Is the table loading.
     */
    tableIsLoading: false as boolean,

    /**
     * Is a save/submit request in progress.
     */
    isSending: false as boolean,

    /**
     * Counter tracking the number of in-flight API requests.
     * Used by apiRequest to auto-reset isSending when all requests complete.
     */
    sendingCount: 0 as number,
  }),

  actions: {
    /**
     * Retrieve the partner.
     */
    async retrievePartner() {
      // Send the retrieve partner request
      const { data } = await apiGet('/partner');

      // Set the partner on the Pinia store
      this.partner = data;
    },

    /**
     * Hide the submenu.
     */
    hideSubmenu(): void {
      this.submenu = null;
    },
  },
});

/**
 * Composable that returns a writable computed ref backed by commonStore.isSending.
 */
export function useIsSending(): WritableComputedRef<boolean> {
  const commonStore = useCommonStore();
  return computed({
    get: () => commonStore.isSending,
    set: (val: boolean) => {
      commonStore.isSending = val;
    },
  });
}
